/**
 * Give a held tile's DECODER back without giving up its picture (issue #14, 2026-10-28).
 *
 * A paused <video> still has its source loaded, and a browser may keep its decoder —
 * a hardware one especially, of which a phone has a handful — reserved for it. Pausing
 * the tiles the budget is holding stops their decode WORK (the dropped frames), but not
 * the decoder count, and "Failed to init decoder" / "Media failed to decode" read like
 * that count running out.
 *
 * So a held tile is PARKED: its current frame is copied to a canvas and set as the
 * element's poster, then the source is removed and `load()` resets the element — which
 * frees the decoder, and the poster is what an element with no data shows. The viewer
 * sees the same still. UNPARK puts the source back, seeks to where it was, and plays if
 * asked. Media is same-origin (/api/media), so the canvas is not tainted; if it ever is,
 * the copy fails quietly and the element keeps whatever poster it had.
 *
 * State lives on the element (a WeakMap), so a slot's remount — a new element — starts
 * clean, and React's own `src` prop is restored to exactly the value it set.
 */
const parked = new WeakMap<HTMLVideoElement, { src: string; t: number }>();

export function isParked(v: HTMLVideoElement | null | undefined): boolean {
  return !!v && parked.has(v);
}

export function park(v: HTMLVideoElement | null | undefined): void {
  if (!v || parked.has(v)) return;
  const src = v.getAttribute("src");
  if (!src) return;
  // The frame, when there is one. readyState >= 2 means a current frame exists.
  if (v.readyState >= 2 && v.videoWidth > 0) {
    try {
      const c = document.createElement("canvas");
      // A still under a paused tile, not an archive: cap the copy so a 1080p tile does not
      // cost a 1080p JPEG encode.
      const scale = Math.min(1, 540 / v.videoHeight);
      c.width = Math.round(v.videoWidth * scale);
      c.height = Math.round(v.videoHeight * scale);
      c.getContext("2d")?.drawImage(v, 0, 0, c.width, c.height);
      v.poster = c.toDataURL("image/jpeg", 0.8);
    } catch {
      /* tainted or out of memory: keep the poster it has */
    }
  }
  parked.set(v, { src, t: v.currentTime || 0 });
  v.pause();
  v.removeAttribute("src");
  v.load(); // with no source: drops the media resource, and with it the decoder
}

/** Undo `park`. Plays once there is data when `play` is true. */
export function unpark(v: HTMLVideoElement | null | undefined, play: boolean): void {
  if (!v) return;
  const p = parked.get(v);
  if (!p) return;
  parked.delete(v);
  if (p.t > 0) {
    v.addEventListener(
      "loadedmetadata",
      () => {
        try {
          v.currentTime = p.t;
        } catch {
          /* a seek refused: from the top is fine */
        }
      },
      { once: true },
    );
  }
  v.src = p.src;
  v.load();
  if (play) void v.play().catch(() => undefined);
}
